import {
  Badge,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Input,
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail
} from '@repo/ui'
import {
  Archive,
  Cable,
  ChevronDown,
  ChevronRight,
  Database,
  FileText,
  FolderOpen,
  History,
  Loader2,
  Mail,
  MessageSquareText,
  MoreHorizontal,
  Pencil,
  Plus,
  Presentation,
  RotateCcw,
  Search,
  Settings,
  Trash2
} from 'lucide-react'
import { Fragment, useEffect, useRef, useState } from 'react'
import type { OrgUser } from '@/api/org/users'
import type { Organization } from '@/components/AppSidebar'
import { DeleteProjectDialog } from '@/components/DeleteProjectDialog'
import { NavUser } from '@/components/NavUser'
import { OrgSwitcher } from '@/components/OrgSwitcher'
import { ProjectDialog } from '@/components/ProjectDialog'
import { useCanvasOutline } from '@/features/conversation/canvasOutline'
import type { ConversationShellController } from './useConversationShell'

type CollectionKind = 'proposals' | 'presentations' | 'content' | 'connectors'
type ArtifactKind = 'proposal' | 'presentation' | 'document'

const collectionRows = [
  ['proposals', 'Proposals', FileText],
  ['presentations', 'Presentations', Presentation],
  ['content', 'Content', Database],
  ['connectors', 'Connectors', Cable]
] as const

export type ConversationRailProps = {
  controller: ConversationShellController
  organizations: Array<Organization>
  currentOrgSlug: string
  orgUsers: Array<OrgUser>
  selectedProjectId?: number
  // `'new'` is the draft: a chat the reader has opened but not yet sent anything into,
  // so it has no identifier to be selected by. See the workspace route's `draftChat`.
  selectedConversationId?: number | 'new'
  selectedCollection?: CollectionKind
  onOpenChat: (projectId: number, conversationId: number | 'new') => void
  onOpenWorkspace: (projectId: number) => void
  onOpenCollection: (kind: CollectionKind) => void
  onOpenArtifact: (
    projectId: number,
    kind: ArtifactKind,
    artifactId: number,
    // Where inside the artifact to open, when the rail's nested outline asks for a
    // particular section or slide rather than the artifact as a whole.
    position?: { section: number } | { slide: number; version: number }
  ) => void
  onOpenPreviousWork: (projectId: number, kind: 'search' | 'marketing', threadId: number) => void
  onOpenCommandSearch: () => void
  onOpenOrganisationSettings: () => void
  onOpenWorkspaceSettings: (projectId: number) => void
}

function importedChatLabel(originKind: 'native' | 'legacy-search' | 'legacy-marketing'): string | undefined {
  if (originKind === 'legacy-search') return 'Search import'
  if (originKind === 'legacy-marketing') return 'Marketing import'
  return undefined
}

// Three states, not two. A collection whose feature is off and a rail with no workspace
// to scope the count to both disable the row, and a reader who cannot see the difference
// is left guessing which of the two they are looking at.
function countLabel(label: string, count?: { available: boolean; count: number }): string {
  if (count === undefined) return `${label}, choose a workspace first`
  return count.available ? `${label}, ${count.count}` : `${label}, unavailable`
}

// The reference gives every group in a workspace body a label and a count, and the
// count is a total rather than the number of visible rows: a bounded list headed
// "Chats" otherwise cannot say what sits behind it.
//
// The chevron sits in a gutter to the left of every label, folding or not, so the groups
// read as one level. A twisty that pushed only its own label right made the group it
// headed look like a child of the group above.
function RailGroup({
  label,
  total,
  hasMore,
  fold
}: {
  label: string
  total: number
  // Set where the count is a loaded page rather than a server total, so the group says
  // "20+" past the bound rather than passing its page off as the whole list.
  hasMore?: boolean
  fold?: { expanded: boolean; onToggle: () => void }
}) {
  const count = `${total}${hasMore === true ? '+' : ''}`
  const className =
    'flex w-full items-center gap-1 px-2 pt-2 pb-1 text-[0.625rem] font-semibold tracking-wide text-muted-foreground uppercase'
  if (fold === undefined) {
    return (
      <p data-testid="conversation-rail-group" className={className}>
        <span className="size-3 shrink-0" aria-hidden="true" />
        <span className="min-w-0 flex-1 truncate">{label}</span>
        <span className="tabular-nums">{count}</span>
      </p>
    )
  }
  return (
    <button
      data-conversation-rail-item
      data-testid="conversation-rail-group"
      type="button"
      // The count goes in the name rather than being left to the eye: a folded group
      // announced by its label alone says nothing about what the fold is holding.
      aria-label={`${label}, ${count}`}
      aria-expanded={fold.expanded}
      className={className}
      onClick={fold.onToggle}
    >
      {fold.expanded ? <ChevronDown className="size-3" /> : <ChevronRight className="size-3" />}
      <span className="min-w-0 flex-1 truncate text-left">{label}</span>
      <span className="tabular-nums">{count}</span>
    </button>
  )
}

const FROM_THIS_CHAT = 'From this chat'
const ELSEWHERE_IN_WORKSPACE = 'Elsewhere in this workspace'

// A chat and an artifact sitting side by side read as related, and most of the time they
// are not: this group belongs to the workspace, so it lists what every chat in it
// produced. The dividers answer that before the click rather than after. They carry no
// count, because only the loaded window can be partitioned while the group's own total
// is the workspace's. An artifact with no loaded origin, and a legacy one with no
// producing chat at all, falls to the second section: absence is not a claim of
// belonging, as with the rail's activity dot.
function artifactSections<T extends { origin?: { conversationId: number } | undefined }>(
  artifacts: Array<T>,
  // A draft has no identifier, so nothing can have come from it, and with no chat open
  // "elsewhere" has nowhere to be elsewhere from. Both render the list undivided.
  selectedConversationId?: number | 'new'
): Array<{ divider?: string; items: Array<T> }> {
  if (typeof selectedConversationId !== 'number') return [{ items: artifacts }]
  return [
    {
      divider: FROM_THIS_CHAT,
      items: artifacts.filter((artifact) => artifact.origin?.conversationId === selectedConversationId)
    },
    {
      divider: ELSEWHERE_IN_WORKSPACE,
      items: artifacts.filter((artifact) => artifact.origin?.conversationId !== selectedConversationId)
    }
  ].filter((section) => section.items.length > 0)
}

// The divider folds the rows it heads, and stays on screen while they are folded: a
// section with its rows away and a workspace that produced nothing would otherwise read
// the same. It still carries no count, for the reason it never had one.
function ArtifactDivider({ label, expanded, onToggle }: { label: string; expanded: boolean; onToggle: () => void }) {
  return (
    <button
      data-conversation-rail-item
      data-testid="conversation-rail-artifact-origin"
      type="button"
      aria-label={label}
      aria-expanded={expanded}
      className="flex w-full items-center gap-1 px-2 pt-1.5 pb-0.5 text-[0.5625rem] font-medium tracking-wide text-muted-foreground"
      onClick={onToggle}
    >
      {expanded ? <ChevronDown className="size-3" /> : <ChevronRight className="size-3" />}
      <span className="min-w-0 flex-1 truncate text-left">{label}</span>
    </button>
  )
}

function focusAfterClose(element: HTMLElement | null): void {
  window.requestAnimationFrame(() => element?.focus())
}

export function ConversationRail({
  controller,
  organizations,
  currentOrgSlug,
  orgUsers,
  selectedProjectId,
  selectedConversationId,
  selectedCollection,
  onOpenChat,
  onOpenWorkspace,
  onOpenCollection,
  onOpenArtifact,
  onOpenPreviousWork,
  onOpenCommandSearch,
  onOpenOrganisationSettings,
  onOpenWorkspaceSettings
}: ConversationRailProps) {
  const { outline } = useCanvasOutline()
  const [showWorkspaceChooser, setShowWorkspaceChooser] = useState(false)
  const [showNewWorkspaceDialog, setShowNewWorkspaceDialog] = useState(false)
  const [deletingWorkspace, setDeletingWorkspace] = useState<{ projectId: number; title: string }>()
  // Collapsing is a view of the rail, not a place the reader is: the open workspace is
  // the URL's, and folding its row away has no business closing the chat and the canvas
  // that sit beside it. So the chevron toggles here and the route is left alone. One id
  // is enough because only the open workspace has a body to fold.
  const [collapsedProjectId, setCollapsedProjectId] = useState<number>()
  // A fold belongs to the visit that made it, and opening a workspace is a new visit
  // however the reader got there: from the rail, the command menu, or a pasted link. A
  // row left folded across one would hide the chat they had just opened.
  const [visitedProjectId, setVisitedProjectId] = useState(selectedProjectId)
  // Resources belong to the workspace whose work they collect. Keep them folded until
  // requested, except when one is already open and hiding it would conceal the current
  // location in the rail.
  const [expandedResourcesProjectId, setExpandedResourcesProjectId] = useState<number>()
  if (visitedProjectId !== selectedProjectId) {
    setVisitedProjectId(selectedProjectId)
    setCollapsedProjectId(undefined)
    setExpandedResourcesProjectId(undefined)
  }
  const [visibleArchivedProjectId, setVisibleArchivedProjectId] = useState<number>()
  const [renaming, setRenaming] = useState<{ projectId: number; conversationId: number; title: string }>()
  const [renameDraft, setRenameDraft] = useState('')
  const newChatButtonRef = useRef<HTMLButtonElement>(null)
  const renameInputRef = useRef<HTMLInputElement>(null)
  const shell = controller.shell
  // The workspace's other artifacts open folded. With a chat on screen the rail is about
  // that chat, and a workspace that has produced a dozen artifacts otherwise buries it
  // under rows belonging to work nobody is doing right now.
  const [artifactsFolded, setArtifactsFolded] = useState(false)
  const [foldedArtifactSections, setFoldedArtifactSections] = useState<ReadonlyArray<string>>([ELSEWHERE_IN_WORKSPACE])
  const toggleArtifactSection = (divider: string) =>
    setFoldedArtifactSections((folded) =>
      folded.includes(divider) ? folded.filter((entry) => entry !== divider) : [...folded, divider]
    )
  // The rail is the open document's navigation, so no fold may hide what the canvas has
  // just opened, nor the outline that navigates it. Opening an artifact unfolds the
  // section it sits in; folding it back afterwards is then the reader's own call.
  const openedArtifact =
    outline === undefined
      ? undefined
      : shell?.expanded?.artifacts.items.find(
          (artifact) => artifact.kind === outline.kind && artifact.id === outline.id
        )
  const outlineKey = outline === undefined ? undefined : `${outline.kind}:${outline.id}`
  const [visitedOutlineKey, setVisitedOutlineKey] = useState(outlineKey)
  if (visitedOutlineKey !== outlineKey) {
    setVisitedOutlineKey(outlineKey)
    if (openedArtifact !== undefined) {
      setArtifactsFolded(false)
      const divider =
        openedArtifact.origin?.conversationId === selectedConversationId ? FROM_THIS_CHAT : ELSEWHERE_IN_WORKSPACE
      setFoldedArtifactSections((folded) => folded.filter((entry) => entry !== divider))
    }
  }

  useEffect(() => {
    if (renaming !== undefined) renameInputRef.current?.focus()
  }, [renaming])

  // Opening a draft is navigation, not a mutation: nothing is written until the first
  // message, so a reader who clicks this and changes their mind leaves no chat behind.
  const openDraftInWorkspace = (projectId: number) => {
    setShowWorkspaceChooser(false)
    focusAfterClose(newChatButtonRef.current)
    onOpenChat(projectId, 'new')
  }

  const onRailKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return
    const items = [...event.currentTarget.querySelectorAll<HTMLElement>('[data-conversation-rail-item]')].filter(
      (item) => !item.hasAttribute('disabled')
    )
    const index = items.indexOf(document.activeElement as HTMLElement)
    if (index < 0) return
    event.preventDefault()
    const offset = event.key === 'ArrowDown' ? 1 : -1
    items[(index + offset + items.length) % items.length]?.focus()
  }

  return (
    <Sidebar
      collapsible="offcanvas"
      aria-label="Snowvault workspace navigation"
      className="overflow-hidden border-r bg-sidebar"
      onKeyDown={onRailKeyDown}
    >
      <SidebarHeader className="gap-2 border-b px-2 py-2">
        <div className="flex min-w-0 items-center gap-1" data-testid="conversation-rail-header-actions">
          <div className="min-w-0 flex-1">
            <OrgSwitcher organizations={organizations} currentOrgSlug={currentOrgSlug} onOrgSwitch={() => undefined} />
          </div>
          <Button
            data-conversation-rail-item
            type="button"
            variant="ghost"
            size="icon-sm"
            className="shrink-0"
            aria-label={`${shell?.organisation.name ?? currentOrgSlug} organisation settings`}
            onClick={onOpenOrganisationSettings}
          >
            <Settings aria-hidden="true" />
          </Button>
        </div>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton
              data-conversation-rail-item
              tooltip="Search or jump to"
              aria-label="Search or jump to"
              onClick={onOpenCommandSearch}
            >
              <Search />
              <span>Search or jump to</span>
              <kbd className="ml-auto rounded border px-1 text-[0.625rem] text-muted-foreground">⌘K</kbd>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>

      <SidebarContent className="overscroll-contain px-2 py-2">
        <div data-testid="conversation-rail-level" className="sr-only">
          New chat
        </div>
        <Button
          ref={newChatButtonRef}
          data-conversation-rail-item
          type="button"
          className="mb-3 w-full justify-start"
          onClick={() => {
            if (selectedProjectId === undefined) setShowWorkspaceChooser(true)
            else openDraftInWorkspace(selectedProjectId)
          }}
        >
          <Plus />
          New chat
        </Button>

        {showWorkspaceChooser && (
          <section
            role="dialog"
            aria-modal="true"
            aria-label="Choose a workspace for your new chat"
            className="mb-3 rounded-lg border bg-background p-2 shadow-sm"
          >
            <p className="mb-2 text-xs font-medium">Choose a workspace for your new chat</p>
            <div className="space-y-1">
              {shell?.workspaces.map((workspace) => (
                <Button
                  key={workspace.projectId}
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="w-full justify-start"
                  onClick={() => openDraftInWorkspace(workspace.projectId)}
                >
                  Start a chat in {workspace.title}
                </Button>
              ))}
            </div>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => {
                setShowWorkspaceChooser(false)
                focusAfterClose(newChatButtonRef.current)
              }}
            >
              Cancel
            </Button>
          </section>
        )}

        <div className="mb-1 flex items-center px-2">
          <div
            data-testid="conversation-rail-level"
            className="text-[0.65rem] font-semibold tracking-[0.16em] text-muted-foreground uppercase"
          >
            Workspaces
          </div>
          <Button
            data-conversation-rail-item
            type="button"
            variant="ghost"
            size="icon-sm"
            className="ml-auto"
            aria-label="New workspace"
            onClick={() => setShowNewWorkspaceDialog(true)}
          >
            <Plus />
          </Button>
        </div>

        {controller.status === 'loading' && (
          <div className="flex items-center gap-2 px-2 py-4 text-xs text-muted-foreground" role="status">
            <Loader2 className="size-3.5 animate-spin" /> Loading workspaces
          </div>
        )}
        {controller.status === 'failure' && (
          <div className="rounded-md border border-destructive/30 p-2 text-xs">
            <p>{controller.error}</p>
            <Button type="button" size="sm" variant="outline" className="mt-2" onClick={controller.retry}>
              Retry
            </Button>
          </div>
        )}
        {shell?.workspaces.length === 0 && (
          <p className="px-2 py-4 text-xs text-muted-foreground">No workspaces yet. Create one to start a chat.</p>
        )}

        <div className="space-y-1">
          {shell?.workspaces.map((workspace) => {
            const selected = workspace.projectId === selectedProjectId
            const expanded = selected && collapsedProjectId !== workspace.projectId
            const extraChats = controller.additionalChats[workspace.projectId] ?? []
            // The shell carries one archive page at a time, for whichever workspace last
            // asked for it. Reading it as this workspace's only when it says so keeps a
            // count off the row it does not belong to.
            const archivedPage = shell.archived?.projectId === workspace.projectId ? shell.archived : undefined
            const archivedShowing = visibleArchivedProjectId === workspace.projectId
            const resourcesExpanded =
              selectedCollection !== undefined || expandedResourcesProjectId === workspace.projectId
            const resourceTotal = collectionRows.reduce((total, [kind]) => {
              const count = workspace.counts[kind]
              return total + (count.available ? count.count : 0)
            }, 0)
            return (
              <section key={workspace.projectId} aria-label={workspace.title} className="rounded-lg">
                {/*
                  The twisty and the name are separate controls because they do separate
                  things. The name is the workspace's address and stays that from every
                  route: the reader reaches the rail from the project page and the content
                  pages too, where the row already reads as open while the workspace itself
                  is not on screen. Folding the row from the same target would have eaten
                  that click and left them where they were.
                */}
                <div
                  className={`group flex w-full items-center gap-1 rounded-md pr-2 ${selected ? 'bg-sidebar-accent text-sidebar-accent-foreground' : ''}`}
                >
                  <Button
                    data-conversation-rail-item
                    type="button"
                    variant="ghost"
                    size="icon-sm"
                    className="shrink-0"
                    aria-label={`${expanded ? 'Collapse' : 'Expand'} ${workspace.title}`}
                    aria-expanded={expanded}
                    onClick={() => {
                      // A workspace that is not open has no body loaded to unfold, so
                      // expanding one is the same act as opening it.
                      if (!selected) onOpenWorkspace(workspace.projectId)
                      else setCollapsedProjectId(expanded ? workspace.projectId : undefined)
                    }}
                  >
                    {expanded ? <ChevronDown className="size-3.5" /> : <ChevronRight className="size-3.5" />}
                  </Button>
                  <Button
                    data-conversation-rail-item
                    type="button"
                    variant="ghost"
                    aria-label={`${workspace.title}, ${workspace.ownership === 'mine' ? 'Mine' : 'Shared'} workspace`}
                    className="h-auto min-w-0 flex-1 justify-start gap-2 px-1 py-2 hover:bg-transparent"
                    onClick={() => onOpenWorkspace(workspace.projectId)}
                  >
                    <span className="min-w-0 flex-1 truncate text-left text-sm font-medium">{workspace.title}</span>
                    <Badge variant="outline" className="h-5 px-1.5 text-[0.625rem] font-normal capitalize">
                      {workspace.ownership === 'mine' ? 'Mine' : 'Shared'}
                    </Badge>
                  </Button>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        data-conversation-rail-item
                        type="button"
                        variant="ghost"
                        size="icon-sm"
                        className={`shrink-0 transition-opacity group-focus-within:opacity-100 group-hover:opacity-100 focus-visible:opacity-100 data-[state=open]:opacity-100 ${selected ? 'opacity-100' : 'opacity-0'}`}
                        aria-label={`Workspace actions for ${workspace.title}`}
                      >
                        <MoreHorizontal aria-hidden="true" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => onOpenWorkspaceSettings(workspace.projectId)}>
                        <Settings aria-hidden="true" /> Settings
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        className="text-destructive focus:text-destructive"
                        onClick={() => setDeletingWorkspace({ projectId: workspace.projectId, title: workspace.title })}
                      >
                        <Trash2 aria-hidden="true" /> Delete workspace
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>

                {expanded && (
                  <div className="ml-4 border-l pl-2">
                    <RailGroup label="Chats" total={workspace.chats.total} />
                    {/*
                      The draft sits where its chat will, so the reader can see what they
                      are typing into. It is not a control: it is already open, and there
                      is nothing yet to rename or archive. The group's total stays the
                      server's count of saved chats, because that is what a reload gives.
                    */}
                    {selectedConversationId === 'new' && (
                      <div
                        data-testid="conversation-rail-draft"
                        aria-current="page"
                        className="flex items-center gap-2 rounded-md bg-sidebar-accent px-2 py-1.5 text-sm"
                      >
                        <MessageSquareText className="size-3.5 shrink-0" aria-hidden="true" />
                        <span className="min-w-0 flex-1 truncate">New chat</span>
                        <span className="shrink-0 text-[0.5625rem] text-muted-foreground uppercase">Draft</span>
                      </div>
                    )}
                    {[...workspace.chats.items, ...extraChats].map((chat) => {
                      const importLabel = importedChatLabel(chat.originKind)
                      // Chats fetched by "show more" come from the chat list endpoint,
                      // which carries no activity. They render without a dot rather than
                      // with a stale one: absence of the mark is not a claim.
                      const live = 'live' in chat && chat.live === true
                      return (
                        <div key={chat.conversationId} className="group/chat flex items-center gap-1">
                          <Button
                            data-conversation-rail-item
                            type="button"
                            variant="ghost"
                            size="sm"
                            className={`min-w-0 flex-1 justify-start px-2 ${chat.conversationId === selectedConversationId ? 'bg-sidebar-accent' : ''}`}
                            // The dot is decoration; the state it marks has to be in the
                            // name, or the rail says nothing to a reader who cannot see it.
                            aria-label={live ? `${chat.title}, working` : undefined}
                            onClick={() => onOpenChat(workspace.projectId, chat.conversationId)}
                          >
                            <MessageSquareText className="size-3.5" />
                            <span className="min-w-0 flex-1 truncate text-left">{chat.title}</span>
                            {importLabel !== undefined && (
                              <span className="shrink-0 text-[0.5625rem] text-muted-foreground">{importLabel}</span>
                            )}
                            {live && (
                              <span
                                data-testid="conversation-rail-live"
                                aria-hidden="true"
                                className="size-1.5 shrink-0 rounded-full bg-primary motion-safe:animate-pulse"
                              />
                            )}
                          </Button>
                          {!chat.readOnly && (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button
                                  data-conversation-rail-item
                                  type="button"
                                  variant="ghost"
                                  size="icon-sm"
                                  className="opacity-0 transition-opacity group-focus-within/chat:opacity-100 group-hover/chat:opacity-100 focus-visible:opacity-100 data-[state=open]:opacity-100"
                                  aria-label={`Chat actions for ${chat.title}`}
                                >
                                  <MoreHorizontal />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="start">
                                <DropdownMenuItem
                                  onClick={() => {
                                    setRenaming({
                                      projectId: workspace.projectId,
                                      conversationId: chat.conversationId,
                                      title: chat.title
                                    })
                                    setRenameDraft(
                                      controller.conflict?.conversationId === chat.conversationId
                                        ? controller.conflict.draftTitle
                                        : chat.title
                                    )
                                  }}
                                >
                                  <Pencil /> Rename chat
                                </DropdownMenuItem>
                                {/* The compatibility home is the deterministic sink for
                                    legacy artifact writes and the database forbids
                                    archiving it, so offering the action here only ever
                                    produced a 409. Renaming it is still allowed. */}
                                {!chat.compatibilityHome && (
                                  <DropdownMenuItem
                                    onClick={() =>
                                      void controller.archiveChat(workspace.projectId, chat.conversationId)
                                    }
                                  >
                                    <Archive /> Archive chat
                                  </DropdownMenuItem>
                                )}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          )}
                        </div>
                      )
                    })}
                    {workspace.chats.hasMore && (
                      <Button
                        data-conversation-rail-item
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="w-full justify-start text-xs text-muted-foreground"
                        aria-label={`Show more chats in ${workspace.title}`}
                        onClick={() => void controller.loadMoreChats(workspace.projectId)}
                      >
                        Show more chats
                      </Button>
                    )}

                    <RailGroup
                      label="Resources"
                      total={resourceTotal}
                      {...(selectedCollection === undefined
                        ? {
                            fold: {
                              expanded: resourcesExpanded,
                              onToggle: () =>
                                setExpandedResourcesProjectId(resourcesExpanded ? undefined : workspace.projectId)
                            }
                          }
                        : {})}
                    />
                    {resourcesExpanded && (
                      <SidebarMenu>
                        {collectionRows.map(([kind, label, Icon]) => {
                          const count = workspace.counts[kind]
                          return (
                            <SidebarMenuItem key={kind}>
                              <SidebarMenuButton
                                data-conversation-rail-item
                                aria-label={countLabel(label, count)}
                                tooltip={label}
                                isActive={selectedCollection === kind}
                                disabled={!count.available}
                                onClick={() => onOpenCollection(kind)}
                              >
                                <Icon />
                                <span>{label}</span>
                                <span className="ml-auto text-xs text-muted-foreground tabular-nums">
                                  {count.available ? count.count : '–'}
                                </span>
                              </SidebarMenuButton>
                            </SidebarMenuItem>
                          )
                        })}
                      </SidebarMenu>
                    )}

                    {/*
                      Archived chats are chats, so the group sits with them rather than
                      after the artifacts, where it read as a third artifact section. A
                      workspace that has archived nothing gets no group: a heading over an
                      empty page is a control that cannot act, and the count answers the
                      question the row was opened to ask.
                    */}
                    {archivedPage !== undefined && archivedPage.items.length > 0 && (
                      <>
                        <RailGroup
                          label="Archived chats"
                          total={archivedPage.items.length}
                          hasMore={archivedPage.hasMore}
                          fold={{
                            expanded: archivedShowing,
                            onToggle: () =>
                              setVisibleArchivedProjectId(archivedShowing ? undefined : workspace.projectId)
                          }}
                        />
                        {archivedShowing && (
                          <div className="rounded-md bg-muted/30 p-1">
                            {archivedPage.items.map((chat) => (
                              <div key={chat.conversationId} className="flex items-center gap-1">
                                <span className="min-w-0 flex-1 truncate px-2 text-xs text-muted-foreground">
                                  {chat.title}
                                </span>
                                <Button
                                  data-conversation-rail-item
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  aria-label={`Restore ${chat.title}`}
                                  onClick={() => void controller.restoreChat(workspace.projectId, chat.conversationId)}
                                >
                                  <RotateCcw /> Restore
                                </Button>
                              </div>
                            ))}
                            {archivedPage.hasMore && (
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => void controller.loadMoreArchived()}
                              >
                                Show more archived chats
                              </Button>
                            )}
                          </div>
                        )}
                      </>
                    )}

                    {/*
                      One group ordered by recency across kinds, with the kind carried by the
                      row icon. A heading per kind cannot order a proposal against a deck, so
                      the list could not answer what the reader was last working on.
                    */}
                    {shell.expanded?.projectId === workspace.projectId && shell.expanded.artifacts.items.length > 0 && (
                      <>
                        <RailGroup
                          label="Workspace artifacts"
                          total={shell.expanded.artifacts.total}
                          fold={{ expanded: !artifactsFolded, onToggle: () => setArtifactsFolded(!artifactsFolded) }}
                        />
                        {!artifactsFolded &&
                          artifactSections(shell.expanded.artifacts.items, selectedConversationId).map((section) => {
                            const divider = section.divider
                            const expandedSection = divider === undefined || !foldedArtifactSections.includes(divider)
                            return (
                              <Fragment key={divider ?? 'undivided'}>
                                {divider !== undefined && (
                                  <ArtifactDivider
                                    label={divider}
                                    expanded={expandedSection}
                                    onToggle={() => toggleArtifactSection(divider)}
                                  />
                                )}
                                {expandedSection &&
                                  section.items.map((artifact) => (
                                    <div key={`${artifact.kind}:${artifact.id}`}>
                                      <Button
                                        data-conversation-rail-item
                                        data-testid="conversation-rail-artifact"
                                        type="button"
                                        variant="ghost"
                                        size="sm"
                                        className="w-full justify-start px-2"
                                        onClick={() => onOpenArtifact(workspace.projectId, artifact.kind, artifact.id)}
                                      >
                                        {artifact.kind === 'presentation' ? (
                                          <Presentation className="size-3.5" />
                                        ) : artifact.kind === 'document' ? (
                                          <Mail className="size-3.5" />
                                        ) : (
                                          <FileText className="size-3.5" />
                                        )}
                                        <span className="truncate">{artifact.title}</span>
                                      </Button>
                                      {/*
                                        The open document carries its outline here, which
                                        is what makes the rail that document's navigation
                                        rather than only the chat's. The canvas publishes
                                        what it already loaded.
                                      */}
                                      {outline?.kind === artifact.kind && outline.id === artifact.id && (
                                        <ul
                                          aria-label={`${artifact.title} outline`}
                                          className="ml-6 flex flex-col border-l pl-1"
                                        >
                                          {outline.entries.map((entry) => (
                                            <li key={entry.ordinal}>
                                              {entry.position === undefined ? (
                                                <span className="flex items-baseline gap-2 px-2 py-1 text-xs text-muted-foreground">
                                                  <span className="tabular-nums">{entry.ordinal}</span>
                                                  <span className="min-w-0 flex-1 truncate">{entry.title}</span>
                                                </span>
                                              ) : (
                                                <Button
                                                  data-conversation-rail-item
                                                  type="button"
                                                  variant="ghost"
                                                  size="sm"
                                                  // The number and the title are separate spans
                                                  // so they can align, which would otherwise run
                                                  // them together in the announced name.
                                                  aria-label={`${entry.ordinal} ${entry.title}`}
                                                  {...(entry.ordinal === outline.activeOrdinal
                                                    ? { 'aria-current': 'location' as const }
                                                    : {})}
                                                  className="h-auto w-full justify-start gap-2 px-2 py-1 text-xs font-normal"
                                                  onClick={() =>
                                                    onOpenArtifact(
                                                      workspace.projectId,
                                                      artifact.kind,
                                                      artifact.id,
                                                      entry.position
                                                    )
                                                  }
                                                >
                                                  <span className="tabular-nums">{entry.ordinal}</span>
                                                  <span className="min-w-0 flex-1 truncate text-left">
                                                    {entry.title}
                                                  </span>
                                                </Button>
                                              )}
                                            </li>
                                          ))}
                                        </ul>
                                      )}
                                    </div>
                                  ))}
                              </Fragment>
                            )
                          })}
                      </>
                    )}

                    {/*
                      Previous work is a workspace's own history, so it belongs in the
                      workspace rather than at the rail's foot where it read as the
                      organisation's. That placement also gives its count a subject.
                    */}
                    {workspace.previousWork.items.length > 0 && (
                      <>
                        <RailGroup label="Previous work" total={workspace.previousWork.total} />
                        {workspace.previousWork.items.map((item) => (
                          <Button
                            key={`${item.kind}:${item.threadId}`}
                            data-conversation-rail-item
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="w-full justify-start px-2"
                            onClick={() => onOpenPreviousWork(item.projectId, item.kind, item.threadId)}
                          >
                            <History className="size-3.5" /> <span className="truncate">{item.title}</span>
                          </Button>
                        ))}
                      </>
                    )}
                  </div>
                )}
              </section>
            )
          })}
        </div>

        {shell?.workspacesHasMore && (
          <Button
            data-conversation-rail-item
            type="button"
            variant="ghost"
            size="sm"
            className="mt-1 w-full justify-start text-muted-foreground"
            aria-label="Show more workspaces"
            onClick={() => void controller.loadMoreWorkspaces()}
          >
            <FolderOpen /> Show more workspaces
          </Button>
        )}

        {controller.operationError !== undefined && (
          <p className="mt-3 rounded-md border border-destructive/30 p-2 text-xs text-destructive" role="alert">
            {controller.operationError}
          </p>
        )}
      </SidebarContent>

      <SidebarFooter className="border-t px-2 py-2">
        <div data-testid="conversation-rail-level" className="sr-only">
          User controls
        </div>
        <NavUser currentOrgSlug={currentOrgSlug} />
      </SidebarFooter>
      <SidebarRail />

      {renaming !== undefined && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Rename chat"
          className="fixed inset-0 z-50 grid place-items-center bg-black/35 p-4"
        >
          <form
            className="w-full max-w-sm rounded-xl border bg-background p-4 shadow-xl"
            onSubmit={(event) => {
              event.preventDefault()
              void controller.renameChat(renaming.projectId, renaming.conversationId, renameDraft).then((updated) => {
                if (updated !== undefined) setRenaming(undefined)
              })
            }}
          >
            <h2 className="text-sm font-semibold">Rename chat</h2>
            <Input
              ref={renameInputRef}
              className="mt-3"
              aria-label="Chat title"
              value={renameDraft}
              onChange={(event) => setRenameDraft(event.target.value)}
            />
            {controller.conflict?.conversationId === renaming.conversationId && (
              <p className="mt-2 text-xs text-warning">
                This chat is now named {controller.conflict.current.title}. Your title is still here so you can retry.
              </p>
            )}
            <div className="mt-4 flex justify-end gap-2">
              <Button
                type="button"
                variant="ghost"
                onClick={() => {
                  setRenaming(undefined)
                  controller.clearConflict()
                }}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={renameDraft.trim() === ''}>
                Save chat title
              </Button>
            </div>
          </form>
        </div>
      )}

      {showNewWorkspaceDialog && (
        <ProjectDialog
          mode="create"
          open={showNewWorkspaceDialog}
          onClose={() => setShowNewWorkspaceDialog(false)}
          currentOrgSlug={currentOrgSlug}
          orgUsers={orgUsers}
        />
      )}
      {deletingWorkspace !== undefined && (
        <DeleteProjectDialog
          open
          projectId={deletingWorkspace.projectId}
          projectTitle={deletingWorkspace.title}
          orgSlug={currentOrgSlug}
          onClose={() => setDeletingWorkspace(undefined)}
        />
      )}
    </Sidebar>
  )
}
